import { memo, useMemo, useState } from 'react';
import SearchInput from '../../SearchInput.jsx';
import FilterSlideoutSection from '../FilterSlideoutSection.jsx';
import FilterSlideoutOptionRow from '../FilterSlideoutOptionRow.jsx';
import {
  FILTER_SLIDEOUT_DISCLOSURE,
  FILTER_SLIDEOUT_OPTION_LIST,
} from '../filterSlideoutTokens.js';
import {
  clearSection,
  getSectionActiveCount,
  getVisibleItems,
  includesValue,
  toggleArrayValue,
} from '../filterSlideout.utils.js';
import { ChevronDown, ChevronUp } from 'lucide-react';

const SearchSelectSection = ({ section }) => {
  const activeCount = getSectionActiveCount(section);
  const [localQuery, setLocalQuery] = useState(section.query ?? '');
  const [showAll, setShowAll] = useState(false);
  const query = section.query ?? localQuery;

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return section.options ?? [];
    return (section.options ?? []).filter((option) =>
      String(option.label).toLowerCase().includes(normalizedQuery),
    );
  }, [query, section.options]);

  const { visibleItems, hiddenCount, hasOverflow } = getVisibleItems(
    filteredOptions,
    section.maxVisible ?? 8,
    showAll,
  );

  const handleQueryChange = (event) => {
    const next = event.target.value;
    setLocalQuery(next);
    setShowAll(false);
    section.onQueryChange?.(next);
  };

  const handleToggle = (option) => {
    if (option.disabled || section.disabled) return;
    section.onChange?.(toggleArrayValue(section.value, option.value));
  };

  return (
    <FilterSlideoutSection
      title={section.title}
      defaultExpanded={section.defaultExpanded ?? true}
      disabled={section.disabled}
      activeCount={activeCount}
      onClear={() => clearSection(section)}
    >
      <div className="flex flex-col gap-3">
        <SearchInput
          value={query}
          onChange={handleQueryChange}
          onClear={() => {
            setLocalQuery('');
            section.onQueryChange?.('');
          }}
          placeholder={section.placeholder ?? 'Search...'}
          className="h-8"
        />

        <div className={FILTER_SLIDEOUT_OPTION_LIST}>
          {section.loading ? (
            <p className="text-xs font-medium leading-5 text-content-muted">Loading...</p>
          ) : visibleItems.length ? (
            visibleItems.map((option) => (
              <FilterSlideoutOptionRow
                key={option.value}
                control="checkbox"
                checked={includesValue(section.value, option.value)}
                disabled={section.disabled || option.disabled}
                label={option.label}
                description={option.description}
                onToggle={() => handleToggle(option)}
              />
            ))
          ) : (
            <p className="text-xs font-medium leading-5 text-content-muted">
              {query ? `No results for ${query}` : (section.emptyText ?? 'No options available')}
            </p>
          )}

          {hasOverflow || showAll ? (
            <button
              type="button"
              className={FILTER_SLIDEOUT_DISCLOSURE}
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll ? 'Show less' : `Show ${hiddenCount} more`}
              {showAll ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          ) : null}
        </div>
      </div>
    </FilterSlideoutSection>
  );
};

export default memo(SearchSelectSection);
